import { WindTunnelForcingProgram } from "../forcing/wind-tunnel";
import type { ForcingProgram } from "../forcing/types";
import { FluidSolver } from "../fluid/solver";
import {
  chooseSimulationSize,
  type FluidImpulse,
} from "../fluid/types";
import {
  sanitizeWord,
  type GlyphInteractionMode,
  type GlyphPlacement,
  type NormalizedPoint,
  WORD_FONT_FAMILY,
  WORD_FONT_WEIGHT,
} from "../typography/layout";
import {
  hitTestGlyphMask,
  moveGlyph,
  TextMaskGenerator,
  type GlyphObstacleMask,
} from "../typography/mask";
import { TextOverlayRenderer } from "../typography/overlay";

type AppState =
  | "running"
  | "dragging-word"
  | "rebuilding-boundary"
  | "suspended";

interface WordDragPointer {
  id: number;
  glyphId: number;
  mode: GlyphInteractionMode;
  dragOffset: NormalizedPoint;
  previousAngle: number;
}

interface FieldPointerSample {
  id: number;
  point: NormalizedPoint;
}

interface AppElements {
  fluidCanvas: HTMLCanvasElement;
  textCanvas: HTMLCanvasElement;
  wordInput: HTMLInputElement;
  windToggle: HTMLButtonElement;
  status: HTMLDivElement;
}

export class AppController {
  private readonly maskGenerator = new TextMaskGenerator();
  private readonly overlay: TextOverlayRenderer;
  private readonly forcing: ForcingProgram;
  private state: AppState = "rebuilding-boundary";
  private word = "FLOW";
  private glyphs: GlyphPlacement[] = [];
  private mask: GlyphObstacleMask | undefined;
  private wordDragPointer: WordDragPointer | undefined;
  private fieldPointerSample: FieldPointerSample | undefined;
  private queuedImpulses: FluidImpulse[] = [];
  private animationFrame = 0;
  private previousFrameTime = 0;
  private elapsed = 0;
  private resizeTimer = 0;
  private inputTimer = 0;
  private boundaryGeneration = 0;
  private reducedMotion = false;
  private windEnabled = true;
  private fatalError = false;

  private constructor(
    private readonly elements: AppElements,
    private readonly solver: FluidSolver,
    forcing?: ForcingProgram,
  ) {
    this.overlay = new TextOverlayRenderer(elements.textCanvas);
    this.forcing = forcing ?? new WindTunnelForcingProgram();
    this.reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    this.syncWindControl();
  }

  static async create(elements: AppElements): Promise<AppController> {
    await document.fonts.load(
      `${WORD_FONT_WEIGHT} 100px ${WORD_FONT_FAMILY}`,
    );
    await document.fonts.ready;

    let controller: AppController | undefined;
    const solver = await FluidSolver.create(elements.fluidCanvas, (message) => {
      controller?.suspendWithError(message);
    });
    controller = new AppController(elements, solver);
    await controller.initialize();
    return controller;
  }

  private async initialize(): Promise<void> {
    this.bindEvents();
    await this.resizeAndRebuild();
    this.showStatus("Ready", false);
    window.setTimeout(() => this.elements.status.classList.add("is-hidden"), 450);
    this.previousFrameTime = performance.now();
    this.animationFrame = requestAnimationFrame(this.frame);
  }

  private bindEvents(): void {
    const canvas = this.elements.fluidCanvas;
    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("pointercancel", this.onPointerCancel);
    canvas.addEventListener("pointerleave", this.onPointerLeave);
    this.elements.wordInput.addEventListener("input", this.onWordInput);
    this.elements.windToggle.addEventListener("click", this.onWindToggle);
    window.addEventListener("resize", this.onResize, { passive: true });
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window
      .matchMedia("(prefers-reduced-motion: reduce)")
      .addEventListener("change", this.onMotionPreferenceChange);
  }

  private readonly frame = (time: number): void => {
    const rawDelta = (time - this.previousFrameTime) / 1000;
    const deltaTime =
      this.previousFrameTime === 0 ? 1 / 60 : Math.min(rawDelta, 0.05);
    this.previousFrameTime = time;

    if (this.state === "running") {
      this.elapsed += deltaTime;
      const ambient = this.windEnabled
        ? this.forcing.update({
            elapsed: this.elapsed,
            deltaTime,
            simulationSize: this.solver.size,
            reducedMotion: this.reducedMotion,
          })
        : [];
      const impulses = [...this.queuedImpulses, ...ambient];
      this.queuedImpulses = [];
      this.solver.step(deltaTime, this.elapsed, impulses);
    } else if (
      this.state === "dragging-word" ||
      this.state === "rebuilding-boundary"
    ) {
      this.solver.render();
    }

    this.animationFrame = requestAnimationFrame(this.frame);
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (
      this.state !== "running" ||
      this.wordDragPointer ||
      event.button !== 0
    ) {
      return;
    }
    const point = this.normalizedPointer(event);
    const glyphId = this.mask
      ? hitTestGlyphMask(this.mask, point.x, point.y)
      : null;
    if (glyphId === null) {
      this.fieldPointerSample = { id: event.pointerId, point };
      return;
    }

    const glyph = this.glyphs.find((placement) => placement.id === glyphId);
    if (!glyph) {
      return;
    }
    const mode = this.overlay.classifyGrab(
      this.word,
      this.glyphs,
      glyphId,
      point,
    );
    this.elements.fluidCanvas.setPointerCapture(event.pointerId);
    this.fieldPointerSample = undefined;
    this.state = "dragging-word";
    this.wordDragPointer = {
      id: event.pointerId,
      glyphId,
      mode,
      dragOffset: {
        x: glyph.center.x - point.x,
        y: glyph.center.y - point.y,
      },
      previousAngle: this.pointerAngle(point, glyph.center),
    };
    this.elements.fluidCanvas.classList.add("is-dragging-word");
    this.elements.fluidCanvas.classList.toggle(
      "is-rotating-glyph",
      mode === "rotate",
    );
    this.overlay.draw(this.word, this.glyphs, { glyphId, mode });
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    const point = this.normalizedPointer(event);
    if (
      this.wordDragPointer &&
      this.wordDragPointer.id === event.pointerId
    ) {
      const glyph = this.glyphs.find(
        (placement) => placement.id === this.wordDragPointer?.glyphId,
      );
      if (!glyph) {
        return;
      }
      if (this.wordDragPointer.mode === "translate") {
        moveGlyph(this.glyphs, this.wordDragPointer.glyphId, {
          x: clamp(point.x + this.wordDragPointer.dragOffset.x, 0.03, 0.97),
          y: clamp(point.y + this.wordDragPointer.dragOffset.y, 0.06, 0.94),
        });
      } else {
        const angle = this.pointerAngle(point, glyph.center);
        glyph.rotation += shortestAngleDelta(
          angle,
          this.wordDragPointer.previousAngle,
        );
        this.wordDragPointer.previousAngle = angle;
      }
      this.overlay.draw(
        this.word,
        this.glyphs,
        {
          glyphId: this.wordDragPointer.glyphId,
          mode: this.wordDragPointer.mode,
        },
      );
      return;
    }

    if (this.state !== "running") {
      this.fieldPointerSample = undefined;
      return;
    }

    const hoveredGlyphId = this.mask
      ? hitTestGlyphMask(this.mask, point.x, point.y)
      : null;
    const hoveredMode =
      hoveredGlyphId === null
        ? null
        : this.overlay.classifyGrab(
            this.word,
            this.glyphs,
            hoveredGlyphId,
            point,
          );
    this.elements.fluidCanvas.classList.toggle(
      "is-word",
      hoveredMode === "translate",
    );
    this.elements.fluidCanvas.classList.toggle(
      "is-rotate",
      hoveredMode === "rotate",
    );
    if (hoveredGlyphId !== null) {
      this.fieldPointerSample = undefined;
      return;
    }

    const previous = this.fieldPointerSample;
    this.fieldPointerSample = { id: event.pointerId, point };
    if (!previous || previous.id !== event.pointerId) {
      return;
    }

    const delta = {
      x: point.x - previous.point.x,
      y: point.y - previous.point.y,
    };
    const distance = Math.hypot(delta.x, delta.y);
    const segments = Math.min(6, Math.max(1, Math.ceil(distance * 45)));
    for (let index = 1; index <= segments; index += 1) {
      const amount = index / segments;
      const sample = {
        x: previous.point.x + delta.x * amount,
        y: previous.point.y + delta.y * amount,
      };
      this.queuedImpulses.push(
        this.pointerImpulse(sample, {
          x: delta.x / segments,
          y: delta.y / segments,
        }),
      );
    }
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (
      !this.wordDragPointer ||
      this.wordDragPointer.id !== event.pointerId
    ) {
      return;
    }
    this.releasePointer(event.pointerId);
    void this.rebuildBoundary();
  };

  private readonly onPointerCancel = (event: PointerEvent): void => {
    if (
      !this.wordDragPointer ||
      this.wordDragPointer.id !== event.pointerId
    ) {
      if (this.fieldPointerSample?.id === event.pointerId) {
        this.fieldPointerSample = undefined;
      }
      return;
    }
    this.releasePointer(event.pointerId);
    void this.rebuildBoundary();
  };

  private readonly onPointerLeave = (): void => {
    if (!this.wordDragPointer) {
      this.fieldPointerSample = undefined;
      this.elements.fluidCanvas.classList.remove("is-word", "is-rotate");
    }
  };

  private readonly onWordInput = (): void => {
    const sanitized = sanitizeWord(this.elements.wordInput.value);
    if (sanitized !== this.elements.wordInput.value) {
      this.elements.wordInput.value = sanitized;
    }
    window.clearTimeout(this.inputTimer);
    this.inputTimer = window.setTimeout(() => {
      const nextWord = sanitizeWord(this.elements.wordInput.value);
      this.word = nextWord;
      this.elements.wordInput.value = nextWord;
      this.glyphs = this.overlay.createPlacements(this.word, {
        x: 0.5,
        y: 0.52,
      });
      this.overlay.draw(this.word, this.glyphs);
      void this.rebuildBoundary();
    }, 220);
  };

  private readonly onResize = (): void => {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      void this.resizeAndRebuild();
    }, 120);
  };

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) {
      if (this.state !== "suspended") {
        this.state = "suspended";
      }
      return;
    }
    if (this.state === "suspended" && !this.fatalError) {
      this.previousFrameTime = performance.now();
      this.state = "running";
    }
  };

  private readonly onMotionPreferenceChange = (
    event: MediaQueryListEvent,
  ): void => {
    this.reducedMotion = event.matches;
    this.syncWindControl();
  };

  private readonly onWindToggle = (): void => {
    this.windEnabled = !this.windEnabled;
    this.syncWindControl();
  };

  private syncWindControl(): void {
    const speedScale = this.windEnabled
      ? this.reducedMotion
        ? 0.22
        : 1
      : 0;
    this.solver.setInletSpeedScale(speedScale);
    this.elements.windToggle.setAttribute(
      "aria-pressed",
      String(this.windEnabled),
    );
    this.elements.windToggle.textContent = this.windEnabled
      ? "Wind on"
      : "Wind off";
    this.elements.windToggle.classList.toggle("is-off", !this.windEnabled);
  }

  private async resizeAndRebuild(): Promise<void> {
    const bounds = this.elements.fluidCanvas.getBoundingClientRect();
    const cssWidth = Math.max(1, Math.round(bounds.width));
    const cssHeight = Math.max(1, Math.round(bounds.height));
    const displayScale = Math.min(window.devicePixelRatio || 1, 2);
    this.elements.fluidCanvas.width = Math.round(cssWidth * displayScale);
    this.elements.fluidCanvas.height = Math.round(cssHeight * displayScale);
    this.overlay.resize(cssWidth, cssHeight);
    if (this.glyphs.length === 0) {
      this.glyphs = this.overlay.createPlacements(this.word, {
        x: 0.5,
        y: 0.52,
      });
    }
    this.overlay.draw(this.word, this.glyphs);
    this.solver.resize(chooseSimulationSize(cssWidth, cssHeight));
    await this.rebuildBoundary();
  }

  private async rebuildBoundary(): Promise<void> {
    if (this.state === "suspended") {
      return;
    }
    const generation = ++this.boundaryGeneration;
    this.state = "rebuilding-boundary";
    this.showStatus("Updating boundary", false);
    this.overlay.draw(this.word, this.glyphs);

    try {
      const nextMask = this.maskGenerator.generate(
        this.word,
        this.glyphs,
        this.solver.size.width,
        this.solver.size.height,
      );
      await this.solver.setObstacle(nextMask);
      if (generation !== this.boundaryGeneration) {
        return;
      }
      this.mask = nextMask;
      this.state = "running";
      this.elements.status.classList.add("is-hidden");
    } catch (error) {
      this.suspendWithError(toErrorMessage(error));
    }
  }

  private pointerImpulse(
    point: NormalizedPoint,
    delta: NormalizedPoint,
  ): FluidImpulse {
    const hue = (this.elapsed * 0.035 + point.x * 0.27 + point.y * 0.13) % 1;
    return {
      point: [point.x, point.y],
      velocity: [
        delta.x * this.solver.size.width * 2.6,
        delta.y * this.solver.size.height * 2.6,
      ],
      radius: Math.max(
        9,
        Math.min(this.solver.size.width, this.solver.size.height) * 0.045,
      ),
      color: hueToRgb(hue),
      dyeAmount: 0.16,
    };
  }

  private normalizedPointer(event: PointerEvent): NormalizedPoint {
    const bounds = this.elements.fluidCanvas.getBoundingClientRect();
    return {
      x: clamp((event.clientX - bounds.left) / bounds.width, 0, 1),
      y: clamp((event.clientY - bounds.top) / bounds.height, 0, 1),
    };
  }

  private pointerAngle(
    point: NormalizedPoint,
    center: NormalizedPoint,
  ): number {
    const bounds = this.elements.fluidCanvas.getBoundingClientRect();
    return Math.atan2(
      (point.y - center.y) * bounds.height,
      (point.x - center.x) * bounds.width,
    );
  }

  private releasePointer(pointerId: number): void {
    if (this.elements.fluidCanvas.hasPointerCapture(pointerId)) {
      this.elements.fluidCanvas.releasePointerCapture(pointerId);
    }
    this.wordDragPointer = undefined;
    this.fieldPointerSample = undefined;
    this.elements.fluidCanvas.classList.remove(
      "is-word",
      "is-rotate",
      "is-dragging-word",
      "is-rotating-glyph",
    );
  }

  private showStatus(message: string, error: boolean): void {
    this.elements.status.textContent = message;
    this.elements.status.classList.toggle("is-error", error);
    this.elements.status.classList.remove("is-hidden");
  }

  private suspendWithError(message: string): void {
    this.fatalError = true;
    this.state = "suspended";
    cancelAnimationFrame(this.animationFrame);
    this.showStatus(message, true);
  }
}

function hueToRgb(hue: number): readonly [number, number, number] {
  const component = (offset: number): number => {
    const value = Math.abs(((hue + offset) * 6) % 6 - 3) - 1;
    return 0.16 + clamp(value, 0, 1) * 0.84;
  };
  return [component(0), component(2 / 3), component(1 / 3)];
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function shortestAngleDelta(current: number, previous: number): number {
  return Math.atan2(
    Math.sin(current - previous),
    Math.cos(current - previous),
  );
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
